import React, { useState } from 'react'
import { useQueryClient } from 'react-query'
import { AppDashboardGridItemWrapper } from '../style'
import {
  AppTabContainerNew,
  AppTab as AppTabNew,
} from 'components/Common/AppButtonTab/AppButtonTabNew.component'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import AppCrossIcon from 'components/Common/AppSvgIcons/AppModalCrossIcon'
import { AppTask } from 'components/AppKanbanBoard/AppTask/AppTask.component'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import {
  BusinessGoalDateFilter,
  UserTask,
  UserTaskQuickFilter,
  UserTaskSource,
} from '__generated__/api-types-and-hooks'
import { useKanbanBoardData } from 'components/AppKanbanBoard/AppHook/AppKanbanBoard.hook'
import { useGetUserTasks } from 'appHooks/useGetUserTasks'
import { ITaskSummaryItem } from 'components/AppDashboard/interfaces'
import { AppTaskCreationConfirmation } from 'components/AppKanbanBoard/AppTaskCreationConfirmation/AppTaskCreationConfirmation.component'
import { IAssigneeOptions, useGetBusinessProfile } from 'appHooks/useGetBusinessProfile'
import { AppTaskCard } from 'components/AppKanbanBoard/AppTaskCard/AppTaskCard.component'
import { categoryType } from 'appConfig/data'
import { ChevronDownIcon } from '@heroicons/react/outline'
import PlusThinIcon from 'components/Common/SvgIcons/PlusThinIcon'
import { AppDeleteTask } from 'components/AppKanbanBoard/AppDeleteTask/AppDeleteTask.component'
import { PlaysSkeletonLoader } from 'components/AppAdvisorDashboard/AppAdvisorPlays/PlaysSkeletonLoader.component'

interface ITasksSummaryBodyItemsProps {
  tasks: ITaskSummaryItem[]
  refetch: () => void
  assigneeOptions: IAssigneeOptions[]
  advisorsData: {
    label: string
    value: string
  }[]
  userAvatar: string
  userFirstName: string
  userLastName: string
  userName: string
  logo: string
}

const AppDashboardTasksSummary = () => {
  const queryClient = useQueryClient()
  const [showAddTaskModal, setShowAddTaskModal] = useState(false)
  const [showAddTaskSuccessModal, setShowAddTaskSuccessModal] = useState(false)
  const [showAddTaskErrorModal, setShowAddTaskErrorModal] = useState(false)
  const [createdUserTask, setCreatedUserTask] = useState<UserTask>({})

  // Use consistent filter approach with playboard for synchronization
  const [filter] = useState<Record<string, (string | number)[] | (string | number)>>({
    dateRange: BusinessGoalDateFilter.EntireGamePlan, // Changed from Today to EntireGamePlan for consistency
  })

  const { userId, tenantId, taskPlan, goalEndDate, assigneeOptions, refetch } = useKanbanBoardData({
    filter,
  })

  const { userAvatar, userFirstName, userLastName, userName, logo, advisorsData } =
    useGetBusinessProfile({})

  // Use consistent data source with playboard instead of separate useGetUserTasks
  const { todoTasks, inProgressTasks, blockedTasks, refetchUserTasks, isUserTasksLoading } =
    useGetUserTasks({
      filter: BusinessGoalDateFilter.EntireGamePlan, // Changed from Today to EntireGamePlan
      quickFilter: UserTaskQuickFilter.AllTasks,
      assigneeId: userId,
    })

  // Enhanced refresh function with cache invalidation for better synchronization
  const handleRefresh = () => {
    refetchUserTasks()
    refetch() // Also refetch playboard data

    // Invalidate all related caches to ensure synchronization between home and playboard
    queryClient.invalidateQueries(['getUserTasks'])
    queryClient.invalidateQueries(['getBusinessGoal'])
    queryClient.invalidateQueries(['getAdvisorAssignedTasks'])
  }

  // Auto-refresh data when component mounts to ensure fresh data
  React.useEffect(() => {
    // Small delay to avoid race conditions
    const timer = setTimeout(() => {
      handleRefresh()
    }, 100)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount
  return (
    <AppDashboardGridItemWrapper>
      <div className="">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-normal font-inter">Plays</h3>
            <h3 className="text-xl font-normal text-app-grey-40 font-inter">Today</h3>
          </div>
          <AppButton
            label="Add a Play"
            variant="primary"
            onClick={() => setShowAddTaskModal(true)}
            size="md"
            RightIcon={() => <PlusThinIcon className="stroke-app-grey-80 ml-2" />}
            // disabled={true}
          />
        </div>
        <div className="mt-8">
          {isUserTasksLoading ? (
            <PlaysSkeletonLoader />
          ) : (
            <>
              {/* {role?.includes(ROLES.BUSINESS_OWNER) ? ( */}
              <AppTabContainerNew tabAlignment="apart">
                <AppTabNew tabName="To Do" tabCount={todoTasks.length}>
                  <TasksSummaryBodyItems
                    tasks={todoTasks}
                    refetch={handleRefresh}
                    assigneeOptions={assigneeOptions}
                    userAvatar={userAvatar ?? ''}
                    userFirstName={userFirstName ?? ''}
                    userLastName={userLastName ?? ''}
                    userName={userName}
                    logo={logo}
                    advisorsData={advisorsData}
                  />
                </AppTabNew>
                <AppTabNew tabName="In Progress" tabCount={inProgressTasks.length}>
                  <TasksSummaryBodyItems
                    tasks={inProgressTasks}
                    refetch={handleRefresh}
                    assigneeOptions={assigneeOptions}
                    userAvatar={userAvatar ?? ''}
                    userFirstName={userFirstName ?? ''}
                    userLastName={userLastName ?? ''}
                    userName={userName}
                    logo={logo}
                    advisorsData={advisorsData}
                  />
                </AppTabNew>
                <AppTabNew tabName="Blocked" tabCount={blockedTasks.length}>
                  <TasksSummaryBodyItems
                    tasks={blockedTasks}
                    refetch={handleRefresh}
                    assigneeOptions={assigneeOptions}
                    userAvatar={userAvatar ?? ''}
                    userFirstName={userFirstName ?? ''}
                    userLastName={userLastName ?? ''}
                    userName={userName}
                    logo={logo}
                    advisorsData={advisorsData}
                  />
                </AppTabNew>
              </AppTabContainerNew>
              {/* ) : (
                <div className="h-[300px] bg-app-white-100 flex items-center justify-center font-medium text-sm">
                  You cannot view this information
                </div>
                )} */}
            </>
          )}
        </div>

        {showAddTaskModal && (
          <AppModalBox
            Icon={AppCrossIcon}
            height="max-h-[835px] h-full"
            width="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] xl:max-w-2xl mx-2 sm:mx-4 md:mx-auto"
            title={`Create new Play`}
            showBorder={false}
            onClose={() => {
              setShowAddTaskModal(false)
            }}
            onBack={() => {
              setShowAddTaskModal(false)
            }}
          >
            <AppTask
              tenantId={tenantId}
              userId={userId}
              plan={taskPlan}
              source={UserTaskSource.Goal}
              setShowTaskModal={setShowAddTaskModal}
              setShowTaskSuccessModal={setShowAddTaskSuccessModal}
              setShowTaskErrorModal={setShowAddTaskErrorModal}
              setCreatedUserTask={setCreatedUserTask}
              refetch={refetchUserTasks}
              logo={logo ?? ''}
              goalEndDate={goalEndDate!}
              advisorsData={advisorsData}
              userName={userName}
              userAvatar={userAvatar ?? ''}
              userFirstName={userFirstName ?? ''}
              userLastName={userLastName ?? ''}
              assigneeOptions={assigneeOptions}
            />
          </AppModalBox>
        )}
        {(showAddTaskSuccessModal || showAddTaskErrorModal) && (
          <AppTaskCreationConfirmation
            showAddTaskSuccessModal={showAddTaskSuccessModal}
            setShowAddTaskModal={setShowAddTaskModal}
            setShowAddTaskSuccessModal={setShowAddTaskSuccessModal}
            setShowAddTaskErrorModal={setShowAddTaskErrorModal}
            createdUserTask={createdUserTask}
            userId={userId}
            userAvatar={userAvatar ?? ''}
            userFirstName={userFirstName ?? ''}
            userLastName={userLastName ?? ''}
            assigneeOptions={assigneeOptions ?? []}
          />
        )}
      </div>
    </AppDashboardGridItemWrapper>
  )
}

const TasksSummaryBodyItems: React.FC<ITasksSummaryBodyItemsProps> = ({
  tasks,
  refetch,
  assigneeOptions,
  userAvatar,
  userFirstName,
  userLastName,
  userName,
  logo,
  advisorsData,
}) => {
  const [showAll, setShowAll] = React.useState(false)
  const [showTaskDetails, setShowTaskDetails] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [taskToDelete, setTaskToDelete] = useState<ITaskSummaryItem | null>(null)
  const displayedTasks = showAll ? tasks : tasks?.slice(0, 2)
  const hasMoreTasks = tasks?.length > 2
  const selectedTask = tasks?.find((task) => task.taskId === selectedTaskId)

  const {
    userAvatar: deleteTaskUserAvatar,
    userFirstName: deleteTaskUserFirstName,
    userLastName: deleteTaskUserLastName,
  } = useGetBusinessProfile({
    paramUserId: taskToDelete?.userId ?? '',
    enabled: taskToDelete ? true : false,
  })
  return (
    <div className="mt-8 overflow-y-auto -mx-6 px-6 h-[340px] overflow-x-hidden">
      {tasks?.length > 0 ? (
        <div className="space-y-4">
          {displayedTasks?.map((task) => (
            <div
              key={task.taskId}
              className="p-4 rounded-xl border border-app-grey-30 shadow-sm min-h-[100px] cursor-pointer"
              onClick={() => {
                setSelectedTaskId(task.taskId ?? null)
                setShowTaskDetails(true)
              }}
            >
              <AppTaskCard
                task={task}
                userId={task.userId ?? ''}
                userAvatar={userAvatar ?? ''}
                userFirstName={userFirstName ?? ''}
                userLastName={userLastName ?? ''}
                onTrashClick={() => {
                  console.log('Trash icon clicked for task:', task.taskId, task)
                  setTaskToDelete(task)
                  setShowDeleteModal(true)
                }}
                refetch={refetch}
                advisorsData={advisorsData}
                assigneeOptions={assigneeOptions}
              />
            </div>
          ))}
          {hasMoreTasks && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="py-2 px-4 flex items-center gap-2 border border-app-grey-30 rounded-lg"
            >
              <span className="text-app-black-100 font-normal text-sm">Show More</span>{' '}
              <ChevronDownIcon className="w-4 h-4 text-app-black-100" />
            </button>
          )}
        </div>
      ) : (
        <div>No results</div>
      )}

      {/* Task Details Modal */}
      {showTaskDetails && selectedTask && (
        <AppModalBox
          Icon={AppCrossIcon}
          height="max-h-[835px] h-full"
          width="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] xl:max-w-2xl mx-2 sm:mx-4 md:mx-auto"
          showBorder={false}
          title={`${selectedTask.category ? `${categoryType[selectedTask.category]} /` : ' '} ${
            selectedTask.name
          } `}
          titleSize="sm"
          onClose={() => {
            setShowTaskDetails(false)
            setSelectedTaskId(null)
          }}
          onBack={() => {
            setShowTaskDetails(false)
            setSelectedTaskId(null)
          }}
        >
          <AppTask
            task={selectedTask}
            logo={logo ?? ''}
            tenantId={selectedTask.tenantId ?? ''}
            source={UserTaskSource.Goal}
            setShowTaskModal={setShowTaskDetails}
            refetch={refetch}
            userId={selectedTask.userId ?? ''}
            userName={userName}
            advisorsData={advisorsData}
            plan={{
              name: selectedTask.planName,
              id: selectedTask.planId,
              planExpectedEndDate: selectedTask.planExpectedEndDate,
            }}
            goalEndDate={''}
            userAvatar={userAvatar ?? ''}
            userFirstName={userFirstName ?? ''}
            userLastName={userLastName ?? ''}
            goalId={selectedTask.goalId ?? ''}
            assigneeOptions={assigneeOptions}
          />
        </AppModalBox>
      )}

      {/* Delete Task Modal */}
      {showDeleteModal && taskToDelete && (
        <AppDeleteTask
          task={taskToDelete as UserTask}
          userId={taskToDelete.userId ?? ''}
          userAvatar={deleteTaskUserAvatar ?? taskToDelete.businessAvatar ?? userAvatar ?? ''}
          userFirstName={deleteTaskUserFirstName ?? userFirstName ?? ''}
          userLastName={deleteTaskUserLastName ?? userLastName ?? ''}
          onCancel={() => {
            setShowDeleteModal(false)
            setTaskToDelete(null)
          }}
          onDeleteClose={async () => {
            console.log('Delete close called, refetching tasks...')
            setShowDeleteModal(false)
            setTaskToDelete(null)
            // Refetch the tasks list to update the UI
            try {
              await refetch()
              console.log('Tasks refetched successfully')
            } catch (error) {
              console.error('Error refetching tasks:', error)
            }
          }}
        />
      )}
    </div>
  )
}

export default AppDashboardTasksSummary
